import type { OBB, Vec2 } from '../../geo/types';

/** A frame on the ground: its `origin`, and its unit axes `across` and `back`, square to each other. */
export interface Frame {
  readonly origin: Vec2;
  readonly across: Vec2;
  readonly back: Vec2;
}

/** A stretch of the frame: across it from `u0` to `u1`, back from `t0` to `t1`. */
export interface Extent {
  readonly u0: number;
  readonly u1: number;
  readonly t0: number;
  readonly t1: number;
}

/** Rows of cells in the frame, each true where the ground at its centre is to be covered. */
interface Cells {
  readonly cols: number;
  readonly rows: number;
  readonly on: Uint8Array;
}

function sample(frame: Frame, extent: Extent, member: (p: Vec2) => boolean, cell: number): Cells {
  const cols = Math.ceil((extent.u1 - extent.u0) / cell);
  const rows = Math.ceil((extent.t1 - extent.t0) / cell);
  const on = new Uint8Array(cols * rows);
  const { origin, across, back } = frame;
  for (let r = 0; r < rows; r++) {
    const t = extent.t0 + (r + 0.5) * cell;
    for (let c = 0; c < cols; c++) {
      const u = extent.u0 + (c + 0.5) * cell;
      const p: Vec2 = [
        origin[0] + across[0] * u + back[0] * t,
        origin[1] + across[1] * u + back[1] * t,
      ];
      if (member(p)) on[r * cols + c] = 1;
    }
  }
  return { cols, rows, on };
}

interface Rect {
  readonly c: number;
  readonly r: number;
  readonly w: number;
  readonly h: number;
}

/*
 * The largest rectangle of cells at least `least` cells each way. Every maximal rectangle rests on
 * some row with the column heights above it, so a stack over each row's heights finds them all.
 */
function largest({ cols, rows, on }: Cells, least: number): Rect | null {
  const heights = new Int32Array(cols);
  let best: Rect | null = null;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) heights[c] = on[r * cols + c] ? heights[c]! + 1 : 0;
    const stack: [number, number][] = [];
    for (let c = 0; c <= cols; c++) {
      const h = c < cols ? heights[c]! : 0;
      let start = c;
      while (stack.length > 0 && stack.at(-1)![1] >= h) {
        const [s, sh] = stack.pop()!;
        const w = c - s;
        if (sh >= least && w >= least && (!best || sh * w > best.w * best.h))
          best = { c: s, r: r - sh + 1, w, h: sh };
        start = s;
      }
      stack.push([start, h]);
    }
  }
  return best;
}

function clear(cells: Cells, rect: Rect): void {
  for (let r = rect.r; r < rect.r + rect.h; r++)
    cells.on.fill(0, r * cells.cols + rect.c, r * cells.cols + rect.c + rect.w);
}

/**
 * Rectangles square to the frame covering the ground `member` names within the extent, largest
 * first, none narrower or shallower than `least` metres; ground they cannot cover is left.
 */
export function rectanglesIn(
  frame: Frame,
  extent: Extent,
  member: (p: Vec2) => boolean,
  cell: number,
  least: number
): OBB[] {
  const cells = sample(frame, extent, member, cell);
  const leastCells = Math.ceil(least / cell - 1e-9);
  const found: OBB[] = [];
  for (let rect = largest(cells, leastCells); rect; rect = largest(cells, leastCells)) {
    clear(cells, rect);
    const u = extent.u0 + (rect.c + rect.w / 2) * cell;
    const t = extent.t0 + (rect.r + rect.h / 2) * cell;
    const { origin, across, back } = frame;
    found.push({
      center: [origin[0] + across[0] * u + back[0] * t, origin[1] + across[1] * u + back[1] * t],
      axisU: across,
      halfU: (rect.w * cell) / 2,
      halfV: (rect.h * cell) / 2,
    });
  }
  return found;
}
